import { Button } from "../ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { WEEKDAYS, iso, monthGrid, monthLabel, todayIso } from "./contractUtils";

/** Month grid for contract visits. `visitsByDate` = { "YYYY-MM-DD": [visit] }.
 * onDayClick(dateStr, visits) fires for every cell; renderVisit(v) draws the chip; selected = Set of ISO dates highlighted. */
export function VisitMonthGrid({ cursor, onCursorChange, visitsByDate, renderVisit, onDayClick, selected, isDisabled, legend, testid = "visit-grid", maxChips = 2, footer }) {
  const { days } = monthGrid(cursor);
  const today = todayIso();
  const move = (n) => onCursorChange(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  return (
    <div className="border border-border rounded-lg bg-card p-3 sm:p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(-1)} data-testid={`${testid}-prev`}><CaretLeft size={16} /></Button>
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold font-[Chivo]" data-testid={`${testid}-month`}>{monthLabel(cursor)}</h2>
          <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => onCursorChange(new Date(new Date().getFullYear(), new Date().getMonth(), 1))} data-testid={`${testid}-today`}>Today</Button>
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => move(1)} data-testid={`${testid}-next`}><CaretRight size={16} /></Button>
      </div>
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
        {WEEKDAYS.map((d) => <div key={d} className="px-1 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const k = iso(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const evs = visitsByDate[k] || [];
          const disabled = isDisabled ? isDisabled(k, evs) : false;
          const isSel = selected?.has(k);
          return (
            <button key={k} type="button" disabled={disabled} onClick={() => onDayClick && onDayClick(k, evs)}
              className={`min-h-[76px] rounded-md border p-1 text-left transition-colors ${isSel ? "border-primary bg-primary/10 ring-1 ring-primary" : "border-border"} ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-muted/40"} ${inMonth ? "" : "opacity-50"}`}
              data-testid={`${testid}-day-${k}`}>
              <span className={`text-[11px] font-medium inline-flex h-5 w-5 items-center justify-center rounded-full ${k === today ? "bg-primary text-primary-foreground" : ""}`}>{d.getDate()}</span>
              <div className="mt-0.5 space-y-0.5">
                {evs.slice(0, maxChips).map((v) => <div key={v.id}>{renderVisit(v)}</div>)}
                {evs.length > maxChips && <div className="text-[9px] text-muted-foreground px-1">+{evs.length - maxChips} more</div>}
              </div>
            </button>
          );
        })}
      </div>
      {legend && <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] text-muted-foreground">{legend}</div>}
      {footer}
    </div>
  );
}

export function VisitChip({ v, showTeam = true }) {
  const st = v.status || "scheduled";
  const cls = st === "completed" ? "bg-emerald-100 text-emerald-800 border-emerald-400 dark:bg-emerald-900/40 dark:text-emerald-300"
    : st === "in_progress" ? "bg-orange-100 text-orange-800 border-orange-400 dark:bg-orange-900/40 dark:text-orange-300"
    : st === "cancelled" ? "bg-slate-100 text-slate-500 border-slate-300 line-through"
    : v.booked_by === "client" ? "bg-violet-100 text-violet-800 border-violet-400 dark:bg-violet-900/40 dark:text-violet-300"
    : "bg-teal-100 text-teal-800 border-teal-400 dark:bg-teal-900/40 dark:text-teal-300";
  const time = v.schedule?.start_time || v.start_time || "";
  const team = v.assignee ?? v.team;
  return (
    <div className={`text-[9px] leading-tight px-1 rounded truncate border-l-2 ${cls} ${v.short_notice && st === "scheduled" ? "ring-1 ring-red-400" : ""}`} title={`${time ? time + " · " : ""}${team || "Unassigned"}${v.short_notice ? " · short notice" : ""}`} data-testid={`visit-chip-${v.id}`}>
      {time ? `${time} ` : ""}{showTeam ? (team || (v.team_assigned === false || v.assignee === "" ? "Unassigned" : "Visit")) : "Visit"}
    </div>
  );
}
